/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
 * filtertool-regel-factory.ts
 * Gegenereerd met Junie (AI), 06-10-2025
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 *
 * Fabrieksfuncties voor het maken en parsen van filtertoolregels zonder circulaire imports.
 */

import {Filtertoolregel, FiltertoolregelType} from './filtertool-regels';
import {FiltertoolProjectregel} from './regels/filtertool-projectregel';
import {FiltertoolPlanitempersoneelRegel} from './regels/filtertool-personeelregel';
import {FiltertoolVrijregel} from './regels/filtertool-vrijregel';
import {FiltertoolPlanitemfunctieRegel} from './regels/filtertool-functieregel';
import {FiltertoolPlanitemfunctiealiasRegel} from './regels/filtertool-functiealiasregel';
import {FiltertoolPersoneelslabelRegel} from './regels/filtertool-personeelslabelregel';
import {FiltertoolPlanitemongekwalificeerdRegel} from './regels/filtertool-ongekwalificeerdregel';

/**
 * Maakt een concrete regelinstantie op basis van plat object. Niet-herkende context geeft null.
 */
export function objectNaarFiltertoolregel(obj_in: any): Filtertoolregel | null {
  if (!obj_in || typeof obj_in !== 'object') return null;
  switch (obj_in.context) {
    case 'project':
      return new FiltertoolProjectregel({ id: String(obj_in.id), naam: obj_in.naam });
    case 'vrij':
      return new FiltertoolVrijregel({ id: String(obj_in.id), naam: obj_in.naam, payload: (obj_in.payload || {}) });
    case 'personeel': // backwards compat: oud type naar nieuwe klasse
    case 'planitempersoneel': {
      const ids = Array.isArray(obj_in.personeelIds) ? obj_in.personeelIds.map((v: any) => Number(v)) : [];
      return new FiltertoolPlanitempersoneelRegel({ id: String(obj_in.id), naam: obj_in.naam, personeelIds: ids });
    }
    case 'personeelslabel': {
      const namen = Array.isArray(obj_in.labelNamen) ? obj_in.labelNamen.map((v: any) => String(v)) : [];
      return new FiltertoolPersoneelslabelRegel({ id: String(obj_in.id), naam: obj_in.naam, labelNamen: namen });
    }
    case 'planitemfunctie': {
      const ids = Array.isArray(obj_in.functieIds) ? obj_in.functieIds.map((v: any) => Number(v)) : [];
      return new FiltertoolPlanitemfunctieRegel({ id: String(obj_in.id), naam: obj_in.naam, functieIds: ids });
    }
    case 'planitemfunctionalias': {
      const waarden = Array.isArray(obj_in.functiealiasWaarden) ? obj_in.functiealiasWaarden.map((v: any) => String(v)) : [];
      return new FiltertoolPlanitemfunctiealiasRegel({ id: String(obj_in.id), naam: obj_in.naam, functiealiasWaarden: waarden });
    }
    case 'planitemongekwalificeerd':
      return new FiltertoolPlanitemongekwalificeerdRegel({ id: String(obj_in.id), naam: obj_in.naam });
    default:
      return null;
  }
}

/**
 * Snel een nieuwe (lege) regel van het gevraagde type maken met standaardwaarden.
 */
export function maakFiltertoolregel(type: FiltertoolregelType): Filtertoolregel {
  switch (type) {
    case 'project':
      return new FiltertoolProjectregel({ naam: 'Projectregel' });
    case 'planitempersoneel':
      return new FiltertoolPlanitempersoneelRegel({ naam: 'Dienst: personeel', personeelIds: [] });
    case 'personeelslabel':
      return new FiltertoolPersoneelslabelRegel({ naam: 'Personeelslabel', labelNamen: [] });
    case 'planitemfunctie':
      return new FiltertoolPlanitemfunctieRegel({ naam: 'Dienst: functie', functieIds: [] });
    case 'planitemfunctionalias':
      return new FiltertoolPlanitemfunctiealiasRegel({ naam: 'Dienst: functionalias', functiealiasWaarden: [] });
    case 'planitemongekwalificeerd':
      return new FiltertoolPlanitemongekwalificeerdRegel({ naam: 'Dienst: ongekwalificeerd personeel' });
    case 'vrij':
    default:
      return new FiltertoolVrijregel({ naam: 'Vrije regel', payload: {} });
  }
}
